// Extended landing-page content for category detail pages (/categories/:slug).
// The two new B2B categories (Automotive, Industrial & Scientific) have full
// intro copy and subcategory groups. Other categories fall back to a generic
// template so every category card links to a working, on-brand page.

export const CATEGORY_DETAILS = {
  'electrical-hvac': {
    seoTitle: 'Electrical & HVAC Components Wholesale | OPHIR SUPPLY',
    seoDescription:
      'OPHIR SUPPLY sources electrical, HVAC and industrial components for distributors, retailers, contractors, institutions and commercial buyers across Canada and the United States.',
    imageAlt: 'Organized wholesale electrical and HVAC components on commercial supply shelving',
    heroIntro:
      'Commercial and industrial electrical, HVAC and power-control products sourced for businesses, distributors, retailers and professional buyers.',
    heading: 'Electrical & HVAC component sourcing for commercial and industrial buyers',
    intro: [
      'OPHIR SUPPLY sources electrical and HVAC components from manufacturers, brand owners and authorized distributors for suitable commercial opportunities across Canada and the United States.',
      'Our focus includes dependable replacement components, maintenance products and professional electrical supplies for wholesale, distribution, e-commerce, institutional and commercial purchasing requirements.',
    ],
    groupsTitle: 'Products We Source',
    groupsSubtitle:
      'We communicate the breadth of what we can source. Availability varies by supplier, brand, quantity and destination — not every product listed is stocked.',
    groups: [
      { title: 'Capacitors', items: ['Run capacitors', 'Dual-run capacitors', 'Motor capacitors', 'HVAC capacitors', 'Commercial replacement capacitors'] },
      { title: 'Contactors & Relays', items: ['HVAC contactors', 'Definite-purpose contactors', 'Control relays', 'Power relays', 'Switching components'] },
      { title: 'Transformers', items: ['Control transformers', 'HVAC transformers', 'Low-voltage transformers', 'Commercial replacement transformers'] },
      { title: 'Circuit Protection', items: ['Circuit breakers', 'Fuses', 'Fuse holders', 'Disconnect components', 'Overcurrent protection'] },
      { title: 'Electrical Controls', items: ['Switches', 'Timers', 'Control modules', 'Pressure switches', 'Control boards'] },
      { title: 'Wiring & Connectivity', items: ['Electrical connectors', 'Terminals', 'Wire connectors', 'Terminal blocks', 'Cable accessories'] },
      { title: 'HVAC Electrical Components', items: ['Capacitors', 'Contactors', 'Relays', 'Transformers', 'Control boards', 'Sensors', 'Pressure switches', 'Sequencers', 'Motor controls'] },
      { title: 'Motors & Motor Components', items: ['HVAC motors', 'Fan motors', 'Blower motors', 'Motor starters', 'Motor controls', 'Replacement motor components'] },
      { title: 'Power & Industrial Components', items: ['Power supplies', 'Industrial controls', 'Electrical enclosures', 'DIN-rail components', 'Switching components', 'Power-management components'] },
      { title: 'Maintenance & Replacement', items: ['Commercial replacement parts', 'Electrical maintenance components', 'HVAC service components', 'MRO electrical products'] },
    ],
    buyerTypes: {
      heading: 'Built for Professional Purchasing',
      body: 'OPHIR SUPPLY supports electrical and HVAC purchasing requirements for businesses and organizations requiring dependable sourcing, commercial quantities and documented supply relationships.',
      items: [
        'Electrical wholesalers and distributors', 'HVAC distributors', 'Mechanical contractors',
        'Electrical contractors', 'Facility maintenance companies', 'Property-management organizations',
        'Industrial facilities', 'Commercial maintenance teams', 'Retailers', 'E-commerce businesses',
        'Institutional purchasing departments', 'Government and public-sector buyers',
        'Healthcare and educational facilities',
      ],
    },
    supplierSection: {
      heading: 'For Electrical & HVAC Manufacturers',
      body: 'OPHIR SUPPLY welcomes opportunities to establish supply relationships with manufacturers, brand owners, importers and authorized distributors of electrical, HVAC and industrial components. We evaluate opportunities based on product quality, documentation, commercial fit, market suitability, purchasing requirements and approved sales channels.',
      ctaLabel: 'Submit Your Product Line',
      ctaTo: '/suppliers',
    },
    cta: {
      heading: 'Need an Electrical or HVAC Product?',
      body: 'Tell us what you are looking for, including manufacturer, part number, specifications and required quantity. Our procurement team can review the requirement and suitable sourcing opportunities.',
      primaryLabel: 'Request a Quote',
      primaryTo: '/request-bulk-quote?category=Electrical%20%26%20HVAC%20Components&source=electrical-hvac',
      secondaryLabel: 'Contact Our Sales Team',
      secondaryTo: '/contact',
    },
  },
  automotive: {
    seoTitle: 'Automotive Wholesale Products | Ophir Supply',
    seoDescription:
      'Explore automotive products available through Ophir Supply, including vehicle maintenance, car care, automotive chemicals, accessories, and other wholesale automotive supplies.',
    imageAlt: 'Wholesale automotive products and vehicle maintenance supplies',
    heading: 'Wholesale Automotive Products for Retail & Commercial Buyers',
    intro: [
      'Ophir Supply distributes established automotive products and everyday vehicle-maintenance essentials to retailers, e-commerce businesses, commercial buyers, and other approved customers.',
      'Our automotive assortment may include vehicle-care products, maintenance supplies, automotive chemicals, lubricants, accessories, replacement products, and other replenishable automotive goods from established manufacturers and suppliers.',
    ],
    groups: [
      {
        title: 'Automotive Maintenance',
        items: ['Motor Oils & Fluids', 'Fuel & Oil Additives', 'Automotive Chemicals', 'Lubricants & Greases'],
      },
      {
        title: 'Car Care',
        items: ['Cleaning & Detailing', 'Windshield & Glass Care', 'Tire & Wheel Care', 'Interior Care', 'Exterior Care'],
      },
      {
        title: 'Parts, Tools & Accessories',
        items: ['Automotive Tools', 'Replacement Parts', 'Filters', 'Batteries & Electrical', 'Automotive Accessories', 'Garage & Shop Supplies'],
      },
    ],
  },
  'industrial-scientific': {
    seoTitle: 'Industrial & Scientific Wholesale Supplies | Ophir Supply',
    seoDescription:
      'Explore industrial, scientific, workplace, maintenance, safety, laboratory, and commercial supplies available through Ophir Supply.',
    imageAlt: 'Wholesale industrial scientific and commercial supplies',
    heading: 'Industrial, Scientific & Commercial Supplies for Business Buyers',
    intro: [
      'Ophir Supply distributes industrial, scientific, workplace, safety, maintenance, and commercial-use products for retailers, e-commerce businesses, institutions, and professional buyers.',
      'Our assortment may include industrial supplies, laboratory products, facility-maintenance products, workplace essentials, safety products, commercial cleaning supplies, tools, and other professional-use goods.',
    ],
    groups: [
      {
        title: 'Industrial, Lab & Scientific',
        items: ['Industrial Supplies', 'Laboratory Supplies', 'Scientific Supplies', 'Measuring & Inspection', 'Material Handling'],
      },
      {
        title: 'Safety & Workplace',
        items: ['Workplace Supplies', 'Safety Products', 'Personal Protective Equipment', 'Janitorial & Sanitation', 'Commercial Cleaning Supplies'],
      },
      {
        title: 'Maintenance, Tools & Consumables',
        items: ['Maintenance Supplies', 'Facility Maintenance', 'Professional Tools', 'Cutting Tools', 'Abrasives', 'Adhesives & Sealants', 'Tapes', 'Fasteners'],
      },
      {
        title: 'Facility & Packaging',
        items: ['Packaging Supplies', 'Electrical Supplies', 'Plumbing Supplies', 'Food Service & Commercial Equipment Supplies'],
      },
    ],
  },
};

// Generic fallback for existing categories so their cards also link to a page.
export const genericDetail = (title) => ({
  seoTitle: `${title} | Ophir Supply`,
  seoDescription: `Explore ${title} products available through Ophir Supply's wholesale, distribution and global-sourcing network.`,
  imageAlt: `Wholesale ${title} products`,
  heading: `Wholesale ${title} for Retail & Commercial Buyers`,
  intro: [
    `Ophir Supply sources and distributes ${title.toLowerCase()} products for retailers, e-commerce businesses, commercial buyers and other approved customers.`,
    'Products available through our supplier network vary by availability, market suitability and agreed purchasing terms. Contact us regarding availability and wholesale opportunities.',
  ],
  groups: [],
});
